import { Link } from '@tanstack/react-router';
import { Fragment, useId, useState } from 'react';

import { Pagination } from '~/components/common/pagination';
import { SearchInput } from '~/components/common/search-input';
import { Skeleton } from '~/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '~/components/ui/tabs';
import { useDebouncedValue } from '~/hooks/use-debounced-value';
import { cn } from '~/utils/cn';

import { BATCH_RUN_PRODUCTS_PAGE_SIZE, useBatchRunProducts } from '../../hooks/use-batch-retry';
import type { BatchCarryover, BatchRun, RetryItemOutcome } from '../../types/batch-retry.api.types';

import { AttemptsToggle, OutcomePill, ProductAttemptsPanel } from './batch-retry-shared';
import { batchLabel, formatCount, humanize, pageRange, SCOPE_LABEL } from './batch-retry-utils';

export type RunFilter = 'ALL' | 'SUCCEEDED' | 'FAILED' | 'NOT_ATTEMPTED' | 'MOVED';

/** Source selector (new batch, original run): all products, only this batch's own, or carried in. */
type SourceFilter = 'ALL' | 'THIS_BATCH' | 'EARLIER';

const SOURCE_OPTIONS: { key: SourceFilter; label: string }[] = [
  { key: 'ALL', label: 'All' },
  { key: 'THIS_BATCH', label: 'This batch' },
  { key: 'EARLIER', label: 'Earlier batches' },
];

/** The carry-over table's "View ›": opens the grid narrowed to one earlier / later batch. */
export interface GridFocus {
  /** Changes on every click so the same selection can be re-applied after the user moved on. */
  key: number;
  batchId: string;
  source?: 'EARLIER';
  outcome?: 'MOVED';
}

const MOVED_FILTER: { key: RunFilter; label: string } = { key: 'MOVED', label: 'Moved' };

const FILTERS: { key: RunFilter; label: string }[] = [
  { key: 'ALL', label: 'All' },
  { key: 'SUCCEEDED', label: 'Enriched' },
  { key: 'FAILED', label: 'Failed' },
];

/** Any run can leave products unreached (the original run, or a retry that was cut short). */
const ALL_FILTERS: { key: RunFilter; label: string }[] = [
  ...FILTERS,
  { key: 'NOT_ATTEMPTED', label: 'Not reached' },
];

/** A filter tab's count: All is what the run covers; the rest are its outcome counts. */
function filterCount(run: BatchRun, key: RunFilter, movedTotal: number): number | null {
  switch (key) {
    case 'ALL':
      return run.picked;
    case 'SUCCEEDED':
      return run.succeeded;
    case 'FAILED':
      return run.failed;
    case 'NOT_ATTEMPTED':
      return run.notAttempted;
    case 'MOVED':
      return movedTotal;
  }
}

export interface RunProductsPanelProps {
  jobId: string;
  run: BatchRun;
  /** Filter the panel opens on (the original run's `?cls=` deep link). */
  initialFilter?: RunFilter;
  /** Carry-over between batches: adds the Source column / selector and the Moved filter. */
  carryover?: BatchCarryover | null;
  /** A carry-over "View ›" selection; only the original run honours it. */
  focus?: GridFocus | null;
}

function priorLabel(outcome: RetryItemOutcome | null, category: string | null): string {
  if (category) return humanize(category);
  if (!outcome) return '—';
  if (outcome === 'NOT_ATTEMPTED') return 'Not reached';
  return outcome === 'SKIPPED' ? 'Skipped' : humanize(outcome);
}

/**
 * Right side of Run Details' runs: the selected run's own products. A retry lists what it did to
 * each product it picked (with its state before); the original run lists how it left each product
 * — later retries don't change that view. What is still not enriched lives on the batch's
 * Needs Attention products page.
 */
export function RunProductsPanel({
  jobId,
  run,
  initialFilter = 'ALL',
  carryover = null,
  focus = null,
}: RunProductsPanelProps) {
  return (
    <section
      data-testid="batch-run-products-panel"
      data-run-id={run.runId}
      className="min-w-0 space-y-4 rounded-lg border p-4"
    >
      <RunProductsBody
        key={`${run.runId}:${initialFilter}:${String(focus?.key ?? '')}`}
        jobId={jobId}
        run={run}
        initialFilter={initialFilter}
        carryover={carryover}
        focus={focus}
      />
    </section>
  );
}

function RunProductsBody({
  jobId,
  run,
  initialFilter,
  carryover,
  focus,
}: {
  jobId: string;
  run: BatchRun;
  initialFilter: RunFilter;
  carryover: BatchCarryover | null;
  focus: GridFocus | null;
}) {
  const isRoot = run.kind === 'ROOT';
  // Carry-over only exists on the original run's view; each piece shows only when there is some.
  const movedTotal = isRoot ? (carryover?.movedOut?.total ?? 0) : 0;
  const hasCarriedIn = isRoot && (carryover?.carriedIn?.total ?? 0) > 0;
  const filters = movedTotal > 0 ? [...ALL_FILTERS, MOVED_FILTER] : ALL_FILTERS;
  const focused = isRoot ? focus : null;
  const [filter, setFilter] = useState<RunFilter>(
    focused?.outcome ?? (filters.some((f) => f.key === initialFilter) ? initialFilter : 'ALL'),
  );
  const [source, setSource] = useState<SourceFilter>(focused?.source ?? 'ALL');
  const [batchId, setBatchId] = useState(focused?.batchId ?? null);
  const focusedBatch =
    batchId === null
      ? null
      : (carryover?.carriedIn?.bySource.find((b) => b.jobId === batchId) ??
        carryover?.movedOut?.byBatch.find((b) => b.jobId === batchId) ??
        null);
  const [page, setPage] = useState(0);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const searchId = useId();
  const q = useDebouncedValue(search.trim(), 300);
  const { data, isLoading, isError } = useBatchRunProducts(
    jobId,
    run.runId,
    {
      ...(filter === 'ALL' ? {} : { outcome: filter }),
      ...(isRoot && source !== 'ALL' ? { source } : {}),
      ...(isRoot && batchId ? { batchId } : {}),
      ...(q ? { q } : {}),
      page,
      size: BATCH_RUN_PRODUCTS_PAGE_SIZE,
    },
    run.status === 'RUNNING',
  );
  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.size)) : 1;
  const title = run.kind === 'ROOT' ? 'Original run' : `Retry #${String(run.attempt)}`;

  return (
    <>
      <header className="space-y-0.5">
        <h3 data-testid="run-products-title" className="text-sm font-semibold">
          {title} · products
        </h3>
        <p data-testid="run-products-scope" className="text-muted-foreground text-xs">
          {isRoot
            ? 'How this run left each product · later retries do not change this view'
            : `${run.scope ? SCOPE_LABEL[run.scope] : 'All products'}${run.reason ? ` · ${run.reason}` : ''}${
                run.status === 'RUNNING' && run.planned !== null && run.planned !== run.picked
                  ? ` · picked ${formatCount(run.picked)} of ${formatCount(run.planned)} so far`
                  : ''
              }`}
        </p>
      </header>
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          {/* Same tab bar as the Catalog Operations grid: sliding primary pill, count badges. */}
          <Tabs
            value={filter}
            onValueChange={(v) => {
              setFilter(v as RunFilter);
              setPage(0);
              setExpanded(null);
            }}
            // Never narrower than its tabs: with Source + search beside it, the row wraps instead
            // of squeezing the tabs into the controls.
            className="min-w-fit flex-1"
          >
            <TabsList
              aria-label="Filter products"
              data-testid="run-products-filter"
              className="w-full justify-evenly gap-1"
            >
              {filters.map((f) => (
                <TabsTrigger
                  key={f.key}
                  value={f.key}
                  data-testid={`run-products-filter-${f.key.toLowerCase()}`}
                  className="w-full px-4"
                >
                  {f.label}
                  <span
                    data-testid={`run-products-count-${f.key.toLowerCase()}`}
                    className={cn(
                      'rounded-full px-1.5 py-0.5 text-xs font-semibold tabular-nums',
                      filter === f.key ? 'text-primary bg-white' : 'bg-primary text-white',
                    )}
                  >
                    {formatCount(filterCount(run, f.key, movedTotal))}
                  </span>
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          {hasCarriedIn && (
            <label className="text-muted-foreground flex items-center gap-2 text-xs">
              Source
              <select
                data-testid="run-products-source"
                value={source}
                className="bg-background h-9 rounded-md border px-2 text-sm"
                onChange={(e) => {
                  setSource(e.target.value as SourceFilter);
                  setBatchId(null);
                  setPage(0);
                  setExpanded(null);
                }}
              >
                {SOURCE_OPTIONS.map((o) => (
                  <option key={o.key} value={o.key}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label htmlFor={searchId} className="sr-only">
            Search this run&apos;s products
          </label>
          <SearchInput
            id={searchId}
            testId="run-products-search"
            value={search}
            onChange={(next) => {
              setSearch(next);
              setPage(0);
              setExpanded(null);
            }}
            placeholder="Search by title or merchant id"
            className="w-full sm:w-80"
            inputClassName="h-9 text-sm"
          />
        </div>

        {batchId && (
          <p
            data-testid="run-products-batch-filter"
            className="text-muted-foreground flex items-center gap-2 text-xs"
          >
            Showing products {filter === 'MOVED' ? 'moved to' : 'from'}{' '}
            <b className="text-foreground">
              {focusedBatch ? batchLabel(focusedBatch) : 'one batch'}
            </b>
            <button
              type="button"
              data-testid="run-products-batch-filter-clear"
              className="text-primary cursor-pointer hover:underline"
              onClick={() => {
                setBatchId(null);
                setPage(0);
              }}
            >
              Clear
            </button>
          </p>
        )}

        {isLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : isError || !data ? (
          <p data-testid="run-products-error" className="text-error text-sm">
            Could not load this run&apos;s products.
          </p>
        ) : data.items.length === 0 ? (
          <p data-testid="run-products-empty" className="text-muted-foreground text-sm">
            {q ? `No products match "${q}".` : 'No products match this filter.'}
          </p>
        ) : (
          <Table
            data-testid="run-products-table"
            containerClassName="max-h-[28rem] rounded-md border"
          >
            <TableHeader className="bg-background sticky top-0 z-10 shadow-[0_1px_0_var(--border)]">
              <TableRow>
                <TableHead>Product</TableHead>
                {hasCarriedIn && <TableHead>Source</TableHead>}
                {!isRoot && <TableHead>Before this run</TableHead>}
                <TableHead>This run</TableHead>
                <TableHead className="text-right" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((p) => (
                <Fragment key={p.productId}>
                  <TableRow data-testid={`run-products-row-${p.productId}`}>
                    <TableCell className="max-w-80">
                      {/* The feed product id is internal; show what people search by, and open
                          the product's own page (it takes the feed product id). */}
                      <Link
                        to="/catalog-operations/$batchId/products/$productId"
                        params={{ batchId: jobId, productId: p.productId }}
                        data-testid={`run-products-open-${p.productId}`}
                        className="text-primary block truncate text-sm hover:underline"
                        title={p.title ?? p.productId}
                      >
                        {p.title ?? p.externalId ?? p.productId}
                      </Link>
                      <span
                        className="text-muted-foreground block truncate font-mono text-xs"
                        title={`Feed product id ${p.productId}`}
                      >
                        {p.externalId ?? p.productId}
                      </span>
                    </TableCell>
                    {hasCarriedIn && (
                      <TableCell
                        data-testid={`run-products-source-${p.productId}`}
                        className="text-muted-foreground text-xs"
                      >
                        {p.fromBatch ? `From ${batchLabel(p.fromBatch)}` : 'This batch'}
                      </TableCell>
                    )}
                    {!isRoot && (
                      <TableCell className="text-muted-foreground text-xs">
                        {priorLabel(p.priorOutcome, p.priorFailureCategory)}
                      </TableCell>
                    )}
                    <TableCell>
                      <div className="space-y-1">
                        {isRoot && p.movedTo ? (
                          // Moved products are no longer this batch's to retry: no actions here,
                          // just where they went and how it turned out.
                          <>
                            <span
                              data-testid={`run-products-moved-${p.productId}`}
                              className="bg-info-50 text-info inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap"
                            >
                              Moved
                            </span>
                            <Link
                              to="/catalog-operations/$batchId"
                              params={{ batchId: p.movedTo.jobId }}
                              data-testid={`run-products-moved-to-${p.productId}`}
                              className="text-primary block text-xs hover:underline"
                            >
                              Moved to {batchLabel(p.movedTo)} ·{' '}
                              {p.movedTo.enriched ? 'enriched there' : 'not enriched there'} ›
                            </Link>
                          </>
                        ) : (
                          <OutcomePill outcome={p.outcome} />
                        )}
                        {p.error && (
                          <p
                            className="text-muted-foreground max-w-xs truncate text-xs"
                            title={p.error}
                          >
                            {p.error}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <AttemptsToggle
                        productId={p.productId}
                        expanded={expanded === p.productId}
                        onToggle={() => {
                          setExpanded((cur) => (cur === p.productId ? null : p.productId));
                        }}
                      />
                    </TableCell>
                  </TableRow>
                  {expanded === p.productId && (
                    <TableRow>
                      <TableCell colSpan={(isRoot ? 3 : 4) + (hasCarriedIn ? 1 : 0)}>
                        <ProductAttemptsPanel jobId={jobId} productId={p.productId} />
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              ))}
            </TableBody>
          </Table>
        )}

        {data && data.total > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span
              data-testid="run-products-range"
              className="text-muted-foreground text-xs tabular-nums"
            >
              {pageRange(page, data.size, data.total)}
            </span>
            <Pagination
              currentPage={page + 1}
              totalPages={totalPages}
              testId="run-products-pagination"
              onPageChange={(next) => {
                setPage(next - 1);
                setExpanded(null);
              }}
            />
          </div>
        )}
      </div>
    </>
  );
}
